"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "./AutoVerkaufen.css";

import BildTextInfo from "../../components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "./sections/TopSection";
import { AutoVerkaufenGrid } from "../../common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import vonZuhauseAus from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-hoechstpreis.avif";
import verifizierteAngebote from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-abholung.avif";
import { fragenAutoVerkauf } from "./data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Ratgeber wird SERVERSEITIG gerendert (dynamic ohne die SSR-Abschaltung,
// Playbook 2.6 / KEYWORD-PLAN): die "Mehr zum Thema"-Kacheln tragen sechs
// interne Links — darunter /auto-verkaufen/auto-export und
// /auto-verkaufen/ratgeber/wirkaufendeinauto-alternative, die sonst in keinem
// Server-HTML dieser Seite stehen. Code-Splitting bleibt erhalten.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function AutoVerkaufen() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title="Auto verkaufen zum Bestpreis"
        linkname="Auto Verkaufen"
        link="/auto-verkaufen"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Verkaufe dein Auto und erhalte mehr Geld.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Auto verkaufen mit Autoeinfachlos"
              leftText={
                <>
                  <p>
                    Autoeinfachlos arbeitet österreichweit mit einem großen
                    Netzwerk an Händlern zusammen, um dein Auto schnell und
                    einfach zum Bestpreis zu verkaufen.
                  </p>
                  <p>
                    Es war noch nie so einfach, den Bestpreis für deinen
                    Gebrauchtwagen zu erhalten.
                  </p>
                  <p>
                    <strong>
                      So funktioniert der Verkauf mit Autoeinfachlos:
                    </strong>
                  </p>
                  <ol className="custom-ordered-list">
                    <li>
                      Trage dein Auto online ein und erhalte eine
                      Sofortbewertung.
                    </li>
                    <li>
                      Erhalte von uns eine objektive Einschätzung, was dein
                      Fahrzeug wert ist.
                    </li>
                    <li>
                      Wir stellen dein Fahrzeug gleichzeitig 5.000 Händlern zur
                      Verfügung.
                    </li>
                    <li>
                      Innerhalb von 24 Stunden erhältst du das höchste Gebot,
                      das die Händler bereit sind zu zahlen.
                    </li>
                    <li>
                      Genieße die kostenlose Abholung direkt bei dir zu Hause
                      und eine sofortige Zahlung direkt auf dein Bankkonto.
                    </li>
                  </ol>
                  <p>
                    <strong>
                      Autoeinfachlos steht für Fairness und Transparenz
                    </strong>
                    &nbsp; das bedeutet keine niedrigen Angebote für dich als
                    Verkäufer und keine minderwertigen Fahrzeuge für unsere
                    Händler.
                  </p>
                  <p>
                    Wir verbinden Verkäufer wie dich mit sorgfältig geprüften
                    Händlern, damit alle eine faire und zufriedenstellende
                    Transaktion erleben. Das ist der Weg zu mehr Geld.
                  </p>
                </>
              }
              rightFirstTitle="Warum ist Autoeinfachlos die beste Plattform zum Autoverkauf?"
              rightFirstText={
                <>
                  <p>
                    Autoeinfachlos.at macht den Autoverkauf in Österreich
                    einfach, schnell und transparent. Anders als
                    Ankaufsplattformen kaufen wir keine Fahrzeuge selbst an,
                    sondern vermitteln dein Auto an über 5.000 geprüfte Händler
                    – so sichern wir dir den bestmöglichen Preis ohne versteckte
                    Kosten und ohne Aufwand. Erhalte eine kostenlose Bewertung
                    und präsentiere dein Auto in unserer täglichen
                    Online-Auktion für den höchsten Gewinn.
                  </p>
                  <p>
                    <strong>Deine Vorteile auf einen Blick:</strong>
                  </p>

                  <ul className="custom-unordered-list">
                    <li>Bestpreis Garantie</li>
                    <li>Verkauf innerhalb von 24 Stunden möglich</li>
                    <li>Kostenlose Abholung direkt bei dir zu Hause</li>
                    <li>Bequem und sicher: Verlasse dein Zuhause nicht</li>
                    <li>
                      Keine Gebühren – der Service ist komplett kostenlos für
                      dich
                    </li>
                    <li>Sofortige Überweisung direkt bei der Abholung</li>
                  </ul>
                </>
              }
              rightSecondTitle="Bereit, dein Auto online zu verkaufen?"
              rightSecondText={
                <>
                  <p>
                    Den Verkauf deines Autos mit Autoeinfachlos erledigst du
                    schnell und einfach über unsere Website.
                  </p>
                  <p>
                    Erstelle ein Fahrzeugprofil, und mehr als 5.000 geprüfte
                    Händler aus ganz Österreich konkurrieren darum, dir ihren
                    besten Preis anzubieten.
                  </p>
                  <p>
                    Nimm das höchste Angebot an, und der Höchstbietende holt
                    dein Auto kostenlos ab und zahlt sofort.
                  </p>
                  <p>
                    Du willst vorher wissen, wie die Abmeldung deines Fahrzeugs
                    abläuft und welche Unterlagen du dafür brauchst?
                  </p>
                  <p>
                    👉{" "}
                    <Link className="link" href="/guides/auto-abmelden">
                      Auto abmelden in Österreich
                    </Link>
                  </p>
                </>
              }
            />
            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Das beste Angebot bekommen"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos eine Alternative zu „Wir kaufen dein Auto“ zu finden."
                text={
                  <>
                    <p>
                      Wenn du das beste Angebot für dein Auto suchst, bist du
                      hier genau richtig. Wenn du dein Auto mit Autoeinfachlos
                      verkaufst, konkurrieren die Händler darum, dir ihren
                      besten Preis zu bieten. Sobald du ein Angebot annimmst,
                      profitierst du von einer kostenlosen Abholung bei dir zu
                      Hause und einer schnellen Überweisung direkt auf dein
                      Bankkonto. Der gesamte Prozess ist einfach und lässt sich
                      bequem vom Handy aus erledigen.
                    </p>
                  </>
                }
              />
              <BildTextInfo
                image={vonZuhauseAus}
                revert
                h2
                fontSize="36px"
                alt="Frau lächelt und schaut auf ihr Smartphone, während sie den Höchstpreis für den Verkauf ihres Autos über Autoeinfachlos ermittelt"
                isBig
                title="Auto verkaufen mit kostenloser Abholung"
                text={
                  <>
                    <p>
                      Ein geprüfter Händler oder dessen Vertreter kommt zu einem
                      für dich passenden Zeitpunkt direkt zu dir - ob nach
                      Hause, an deinen Arbeitsplatz oder an eine gewünschte
                      Adresse. Dort begutachtet er dein Fahrzeug, um
                      sicherzustellen, dass es dem zuvor beschriebenen Zustand
                      entspricht.
                    </p>
                    <p>
                      Sobald der Händler bestätigt, dass dein Fahrzeug wie
                      beschrieben ist, überweist er den Betrag direkt auf dein
                      Bankkonto. Anschließend nimmt er dein Auto gleich mit. So
                      einfach ist es wirklich: Auto raus, Geld rein.
                    </p>

                    <p>
                      Es gibt keine Filialen-Besuche, der gesamte Prozess läuft
                      digital ab.
                    </p>
                  </>
                }
              />
              <BildTextInfo
                image={verifizierteAngebote}
                h2
                fontSize="36px"
                title="Auto verkaufen an einen geprüften Händler"
                alt="Ein Smartphone mit einem Verkaufsangebot für ein Auto, als Alternative zur Plattform Wir kaufen dein Auto"
                text={
                  <>
                    <p>
                      Bei Autoeinfachlos kooperieren wir mit über 76% aller
                      Händler in Österreich. Dadurch erhältst du die
                      Möglichkeit, mit nur einem Klick tausende von Händlern zu
                      erreichen und dir das beste Angebot einzuholen. Es ist,
                      als würdest du nahezu alle Händler in Österreich besuchen,
                      um den Höchstbietenden zu finden – nur eben mit einem
                      Klick.{" "}
                    </p>
                  </>
                }
              />
            </div>

            {/* <div>Trustpolit kommt hier</div> */}
            <HäufigeFragen
              title="Häufig gestellte Fragen zum Autoverkauf"
              fragen={fragenAutoVerkauf}
              image={FAQImage}
            />

            <Ratgeber
              title="Mehr zum Thema Auto verkaufen"
              autoBewerten
              listenpreisBerechnen
              fahrgestellnummerPruefen
              kaputtesAuto
              autoExport
              wirkaufendeinautoAlternative
            />
          </div>
          <Banner />
        </div>
      )}
    </div>
  );
}

export default AutoVerkaufen;
